import { useState, useMemo, useLayoutEffect } from "react";
import { Portfolio } from "api/initial/useGetContactInfo";
import { SecurityCode } from "api/securities";
import { TradableSecurity } from "api/trading/useGetTradebleSecurities";
import { WhiteListPayload } from "api/transfers/useGetWhiteListedAddresses";
import classNames from "classnames";
import { CryptoLogo } from "components";
import { Select } from "components/Select/Select";
import { BASE_PAPER_CLASS } from "./Paper";

export interface SecuritySelectConfig {
  whiteListedAddresses?: WhiteListPayload[]; // If provided, only show securities that are whitelisted for the portfolio
  portfolio?: Portfolio;
  allowedSecurityCodes?: SecurityCode[]; // If provided, only show securities that are allowed
  initialSecurityId?: number;
}

export const useSecuritySelect = (
  securities: TradableSecurity[] | undefined,
  config?: SecuritySelectConfig
) => {
  const [selectedSecurityOpt, setSelectedSecurityOpt] =
    useState<SecurityOption | null>(null);

  const securityOptions = useMemo(() => {
    const opts: SecurityOption[] = [];

    securities?.forEach((item) => {
      const isAllowed =
        !config?.allowedSecurityCodes ||
        config.allowedSecurityCodes.includes(item.securityCode as SecurityCode);

      const isWhitelisted =
        !config?.whiteListedAddresses || !config?.portfolio
          ? true
          : config?.whiteListedAddresses.some(
              (addr) =>
                addr.securityCode === item.securityCode &&
                addr.portfolioShortName === config?.portfolio?.shortName
            );

      if (isAllowed && isWhitelisted) {
        opts.push({
          id: item.id.toString(),
          label: item.name,
          securityCode: item.securityCode,
        });
      }
    });

    return opts;
  }, [
    securities,
    config?.allowedSecurityCodes,
    config?.whiteListedAddresses,
    config?.portfolio,
  ]);

  const selectedSecurity = useMemo(() => {
    if (!selectedSecurityOpt) return null;

    return (
      securities?.find(
        (security) => security.id.toString() === selectedSecurityOpt.id
      ) || null
    );
  }, [selectedSecurityOpt, securities]);

  useLayoutEffect(() => {
    if (!selectedSecurityOpt && securityOptions.length > 0) {
      const defaultSecurity = securityOptions[0];
      const initialSecurityId = config?.initialSecurityId;

      if (initialSecurityId) {
        const initialSecurity = securityOptions.find(
          (security) => security.id === initialSecurityId.toString()
        );

        setSelectedSecurityOpt(initialSecurity ?? defaultSecurity);
      } else {
        setSelectedSecurityOpt(defaultSecurity);
      }
    }
  }, [securityOptions, selectedSecurityOpt, config?.initialSecurityId]);

  return {
    selectedSecurityOpt,
    setSelectedSecurityOpt,
    securityOptions,
    selectedSecurity,
  };
};

export type SecurityOption = {
  id: string;
  label: string;
  securityCode: string;
};

interface SecuritySelectProps {
  securityOptions: SecurityOption[];
  onSecurityChange: (value: SecurityOption) => void;
  selectedSecurityOpt: SecurityOption | null;
}

const SecuritySelect = ({
  securityOptions,
  onSecurityChange,
  selectedSecurityOpt,
}: SecuritySelectProps) => {
  return (
    <Select
      options={securityOptions}
      value={selectedSecurityOpt || { id: "", label: "", securityCode: "" }}
      onChange={(value) => onSecurityChange(value)}
      buttonClassName={classNames(BASE_PAPER_CLASS, "h-[60px] md:h-[100px]")}
      labelClassName="text-xl md:text-2xl"
      preLabel={
        <CryptoLogo
          name={selectedSecurityOpt?.securityCode}
          className="ml-2 w-[30px] h-[30px] md:w-[50px] md:h-[50px]"
        />
      }
    />
  );
};

export default SecuritySelect;
